import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import { LogOut, DollarSign, Fuel, MoreHorizontal, X, Lock } from "lucide-react";
import { NAV_ITEMS, isItemOfflineAvailable } from "./Sidebar";
import useOnline from "@/hooks/useOnline";
import { useConfirm } from "@/lib/confirm";

// Primary destinations shown directly in the bottom nav per role.
// All other NAV_ITEMS go into the "Más" sheet. Pick 4 most-used per role
// to avoid scroll and keep tap targets large.
const PRIMARY_BY_ROLE = {
  super_admin: ["/superadmin", "/companies", "/all-users", "/admin/alerts"],
  ops_manager: ["/dashboard", "/calendar", "/alerts", "/fleet"],
  pilot: ["/dashboard", "/alerts", "/calendar", "/flights"],
  owner: ["/calendar", "/alerts", "/fleet", "/tracking"],
  mechanic: ["/alerts", "/calendar", "/fleet", "/maintenance"],
  ground_support: ["/alerts", "/calendar", "/fuel-orders", "/tracking"],
};

export default function BottomNav({ alertCount = 0 }) {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const activeRef = useRef(null);
  const [moreOpen, setMoreOpen] = useState(false);

  const online = useOnline();
  const baseItems = NAV_ITEMS[user?.role] || NAV_ITEMS.pilot;
  const ownerItems =
    user?.role === "owner"
      ? [
          ...baseItems,
          ...(user?.settings?.can_see_expenses
            ? [
                {
                  path: "/expenses",
                  labelKey: "nav.expenses",
                  icon: DollarSign,
                  offlineAvailable: true,
                },
              ]
            : []),
          ...(user?.settings?.can_see_fuel_analytics
            ? [{ path: "/fuel-analytics", labelKey: "nav.fuelAnalytics", icon: Fuel }]
            : []),
        ]
      : baseItems;

  // Aplanado SÓLO para resolver los 4 destinos de la barra por su ruta.
  const flattened = ownerItems.flatMap((item) => (item.children ? item.children : [item]));

  const primaryPaths = PRIMARY_BY_ROLE[user?.role] || PRIMARY_BY_ROLE.pilot;
  const primaryItems = primaryPaths.map((p) => flattened.find((i) => i.path === p)).filter(Boolean);

  // El cajón "Más" CONSERVA los grupos (Reportes, Personas). Antes se aplanaba
  // aquí también, y al coordinador le quedaban 11 renglones planos sin un solo
  // encabezado, mientras el sidebar de escritorio sí los mostraba agrupados:
  // la estructura ya existía en NAV_ITEMS y el celular la tiraba
  // (revisión de UX 2026-07-26). Los sueltos van arriba, sin encabezado.
  const overflowGroups = [];
  const sueltos = [];
  for (const item of ownerItems) {
    if (item.children) {
      const hijos = item.children.filter((c) => !primaryPaths.includes(c.path));
      if (hijos.length) overflowGroups.push({ labelKey: item.labelKey, items: hijos });
    } else if (!primaryPaths.includes(item.path)) {
      sueltos.push(item);
    }
  }
  const overflowSections = [
    ...(sueltos.length ? [{ labelKey: null, items: sueltos }] : []),
    ...overflowGroups,
  ];
  const overflowCount = overflowSections.reduce((n, s) => n + s.items.length, 0);

  const closeSheet = () => setMoreOpen(false);
  useEffect(() => {
    if (moreOpen) {
      const onKey = (e) => {
        if (e.key === "Escape") closeSheet();
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }
  }, [moreOpen]);

  const renderTab = (item) => {
    const Icon = item.icon;
    const active = location.pathname === item.path;
    const showBadge = item.isAlerts && alertCount > 0;
    const locked = !online && !isItemOfflineAvailable(item);
    return (
      <button
        key={item.path || item.labelKey}
        ref={active ? activeRef : null}
        onClick={() => {
          if (!locked) navigate(item.path);
        }}
        title={locked ? t("offline.requiresConnection") : undefined}
        disabled={locked}
        className={`flex-1 flex flex-col items-center justify-center gap-0.5 relative transition-all duration-150 ${
          locked ? "opacity-40 cursor-not-allowed" : "active:scale-95 active:opacity-80"
        }`}
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
        {active && !locked && (
          <span className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-white rounded-b-full" />
        )}
        <div className="relative">
          <Icon
            className={`w-[22px] h-[22px] transition-colors duration-150 ${
              locked ? "text-slate-600" : active ? "text-white" : "text-slate-500"
            }`}
          />
          {locked ? (
            <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-slate-800 rounded-full flex items-center justify-center">
              <Lock className="w-2.5 h-2.5 text-slate-400" />
            </span>
          ) : (
            showBadge && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 bg-red-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                {alertCount > 9 ? "9+" : alertCount}
              </span>
            )
          )}
        </div>
        {/* Etiqueta corta si existe. En 64 px caben ~12 caracteres a 10 px, y
            "Mantenimiento" son 13: salía "Mantenim…" en la barra del mecánico,
            que es el rol al que esa pestaña le importa. Ensanchar la celda
            rompería la rejilla de cinco iconos, así que la barra usa un texto
            propio —"Taller"— y el menú completo conserva el nombre largo.
            `defaultValue` deja que cualquier otra entrada siga usando el suyo
            sin tener que declarar una clave corta. */}
        <span
          className={`text-[10px] font-medium tracking-tight leading-none transition-colors duration-150 max-w-[64px] truncate px-1 ${
            locked ? "text-slate-600" : active ? "text-white" : "text-slate-400"
          }`}
        >
          {t(`${item.labelKey}Short`, { defaultValue: t(item.labelKey) })}
        </span>
      </button>
    );
  };

  return (
    <>
      <nav
        className={`bottom-nav-bar lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t
          ${theme === "light" ? "bg-[#f6f8fc] border-[#d4dce8]" : "bg-[#0f172a] border-slate-800"}`}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-stretch h-16">
          {primaryItems.map(renderTab)}
          {overflowCount > 0 && (
            <button
              onClick={() => setMoreOpen(true)}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 relative transition-all duration-150 active:scale-95 active:opacity-80"
              style={{ WebkitTapHighlightColor: "transparent" }}
            >
              <MoreHorizontal className="w-[22px] h-[22px] text-slate-400" />
              <span className="text-[10px] font-medium tracking-tight leading-none text-slate-400 max-w-[64px] truncate px-1">
                {t("nav.more")}
              </span>
            </button>
          )}
        </div>
      </nav>

      {/* "Más" sheet — overflow destinations + sign out */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-[55]" onClick={closeSheet}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className={`bottom-nav-sheet absolute bottom-0 left-0 right-0 flex flex-col ${theme === "light" ? "bg-[#f6f8fc]" : "bg-[#0f172a]"} border-t ${
              theme === "light" ? "border-[#d4dce8]" : "border-slate-800"
            } rounded-t-2xl pb-[calc(8px+env(safe-area-inset-bottom,0px))] animate-slide-up`}
            style={{
              maxHeight: "calc(100svh - max(env(safe-area-inset-top, 0px), 16px) - 16px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-2.5 pb-1.5 flex items-center justify-between border-b border-slate-800 flex-shrink-0">
              <span className="text-sm font-semibold text-white">{t("nav.more")}</span>
              <button
                onClick={closeSheet}
                className="w-11 h-11 rounded-xl bg-slate-700/40 text-slate-300 hover:bg-slate-600/60 hover:text-white active:bg-slate-600/60 active:text-white active:scale-95 transition-all flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Lista vertical en lugar de grid 3 cols: con grid los nombres
                largos se truncaban a "Mantenimi..." / "Inspeccio..." y no se
                entendía. Como lista cada label tiene todo el ancho y se ve
                completo. */}
            <div className="flex flex-col p-2 overflow-y-auto flex-1 min-h-0">
              {overflowSections.map((section) => (
                <div key={section.labelKey || "_sueltos"} className="flex flex-col">
                  {section.labelKey && (
                    <span
                      data-testid={`nav-more-group-${section.labelKey}`}
                      className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-400 px-3 pt-3 pb-1"
                    >
                      {t(section.labelKey)}
                    </span>
                  )}
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = location.pathname === item.path;
                    const locked = !online && !isItemOfflineAvailable(item);
                    return (
                      <button
                        key={item.path || item.labelKey}
                        onClick={() => {
                          if (locked) return;
                          navigate(item.path);
                          closeSheet();
                        }}
                        disabled={locked}
                        title={locked ? t("offline.requiresConnection") : undefined}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                          locked
                            ? "text-slate-400 cursor-not-allowed opacity-60"
                            : active
                              ? "bg-[#0052FF]/15 text-white"
                              : "text-slate-300 active:bg-slate-700/40"
                        }`}
                      >
                        <Icon
                          className={`w-5 h-5 flex-shrink-0 ${
                            locked ? "text-slate-500" : active ? "text-[#4d8cff]" : "text-slate-400"
                          }`}
                        />
                        <span className="text-sm font-medium text-left flex-1">
                          {t(item.labelKey)}
                        </span>
                        {locked && <Lock className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="px-4 pb-2 flex-shrink-0">
              <button
                onClick={async () => {
                  const ok = await confirm({
                    title: t("auth.signOut"),
                    message: t("auth.signOutConfirm"),
                    confirmLabel: t("auth.signOut"),
                    tone: "danger",
                  });
                  if (ok) {
                    logout();
                    closeSheet();
                  }
                }}
                data-testid="mobile-logout-btn"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 active:bg-red-500/20 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">{t("auth.signOut")}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
